import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, User, Bot, Loader2, Mail, Phone, ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`.replace(/^http/, 'ws');

export default function ChatWidget({ user }) {
  const [isOpen, setIsOpen] = useState(false);

  // Listen for external open-chat events
  useEffect(() => {
    const handleOpenChat = () => { setIsOpen(true); setIsMinimized(false); };
    window.addEventListener('open-live-chat', handleOpenChat);
    return () => window.removeEventListener('open-live-chat', handleOpenChat);
  }, []);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chatStatus, setChatStatus] = useState('active');
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const sessionIdRef = useRef(null);

  // Load existing session from localStorage
  useEffect(() => {
    const savedSession = localStorage.getItem('chatSessionId');
    if (savedSession) {
      loadChatHistory(savedSession);
    }
  }, []);

  // Keep sessionIdRef in sync
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // WebSocket connection for real-time agent messages
  const connectWs = useCallback((sid) => {
    if (!sid) return;
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    const ws = new WebSocket(`${WS_BASE}/chat/ws/${sid}`);
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      try {
        const event = JSON.parse(evt.data);
        if (event.type === 'new_message' && event.message) {
          setMessages(prev => {
            if (prev.some(m => m.timestamp === event.message.timestamp && m.content === event.message.content)) return prev;
            return [...prev, event.message];
          });
        }
        if (event.type === 'session_update' && event.status) {
          setChatStatus(event.status);
        }
      } catch {}
    };

    ws.onclose = () => {
      wsRef.current = null;
      // Reconnect if session still active
      if (sessionIdRef.current === sid) {
        reconnectTimer.current = setTimeout(() => connectWs(sid), 3000);
      }
    };

    ws.onerror = () => ws.close();

    // Ping to keep alive
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      else clearInterval(ping);
    }, 25000);
    ws._pingInterval = ping;
  }, []);

  // Connect WebSocket when sessionId is set
  useEffect(() => {
    if (sessionId) connectWs(sessionId);
    return () => {
      if (wsRef.current) {
        clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [sessionId, connectWs]);

  // Scroll to bottom when messages change, but don't steal focus
  const isInputFocused = useRef(false);
  useEffect(() => {
    if (!isInputFocused.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Scroll without stealing focus
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [messages]);

  // Focus input when chat opens — skip on touch devices so the mobile
  // keyboard doesn't open automatically and push the chat off-screen.
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const isTouch = typeof window !== 'undefined'
      && (window.matchMedia?.('(hover: none) and (pointer: coarse)').matches
        || window.innerWidth < 640);
    if (isTouch) return;
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, isMinimized]);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API}/chat/history/${sid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          setSessionId(sid);
          setChatStatus(data.session?.status || 'active');
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const startNewChat = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/chat/start`, {
        method: 'POST',
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session.session_id);
        setMessages(data.messages);
        setChatStatus('active');
        localStorage.setItem('chatSessionId', data.session.session_id);
      }
    } catch (error) {
      console.error('Failed to start chat:', error);
      toast.error('Failed to start chat');
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    setIsMinimized(false);
    if (!sessionId && messages.length === 0) {
      startNewChat();
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      sender_name: user?.display_name || 'You',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsTyping(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/chat/message`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: userMessage.content,
          session_id: sessionId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update session ID if new
        if (data.session_id && !sessionId) {
          setSessionId(data.session_id);
          localStorage.setItem('chatSessionId', data.session_id);
        }

        // Add AI/agent response only if not already added by WebSocket
        if (data.response) {
          setMessages(prev => {
            if (prev.some(m => m.timestamp === data.response.timestamp && m.content === data.response.content)) return prev;
            return [...prev, data.response];
          });
        }

        // Update status if escalated
        if (data.status === 'escalated') {
          setChatStatus('escalated');
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const handleEscalate = async () => {
    try {
      const response = await fetch(`${API}/chat/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          reason: 'User requested human agent'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setChatStatus('escalated');
        setMessages(prev => [...prev, {
          role: 'system',
          content: data.message,
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Failed to escalate:', error);
      toast.error('Failed to connect to agent');
    }
  };

  const handleEmailTranscript = async () => {
    if (!emailInput.trim()) return;

    try {
      const response = await fetch(`${API}/chat/email-transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: emailInput.trim()
        })
      });

      if (response.ok) {
        toast.success('Transcript sent to your email');
        setShowEmailForm(false);
        setEmailInput('');
      } else {
        toast.error('Failed to send transcript');
      }
    } catch (error) {
      console.error('Failed to send transcript:', error);
      toast.error('Failed to send transcript');
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem('chatSessionId');
    setSessionId(null);
    setMessages([]);
    setChatStatus('active');
    startNewChat();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Chat button (closed state)
  if (!isOpen) {
    return (
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 bg-[#007a33] hover:bg-[#005c26] text-white rounded-full p-3 shadow-lg transition-all duration-300 hover:scale-110"
        data-testid="chat-widget-button"
        aria-label="Open support chat"
        title="Chat with us"
      >
        <MessageCircle className="w-5 h-5" />
      </button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-[#007a33] hover:bg-[#005c26] text-white rounded-full px-4 py-3 shadow-lg flex items-center gap-2 transition-all duration-300"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Chat</span>
          {messages.length > 0 && (
            <span className="bg-white text-[#007a33] text-xs rounded-full px-2 py-0.5">
              {messages.filter(m => m.role === 'assistant' || m.role === 'agent').length}
            </span>
          )}
        </button>
      </div>
    );
  }

  // Full chat window
  return (
    <div 
      className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 z-50 w-auto sm:w-[380px] sm:max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
      style={{ height: '550px', maxHeight: 'calc(100vh - 6rem)' }}
      data-testid="chat-widget"
    >
      {/* Header */}
      <div className="bg-[#007a33] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Grassroots Support</h3>
            <p className="text-xs text-white/80">
              {chatStatus === 'escalated' ? 'Connected to agent' : 'AI Assistant'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button 
            onClick={() => setIsMinimized(true)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button 
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                msg.role === 'user'
                  ? 'bg-[#007a33] text-white rounded-br-md'
                  : msg.role === 'system'
                  ? 'bg-yellow-100 text-yellow-800 text-center text-sm italic w-full max-w-full rounded-lg'
                  : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
              }`}
            >
              {msg.role !== 'user' && msg.role !== 'system' && (
                <div className="flex items-center gap-1.5 mb-1">
                  {msg.role === 'agent' ? (
                    <User className="w-3 h-3 text-[#007a33]" />
                  ) : (
                    <Bot className="w-3 h-3 text-[#007a33]" />
                  )}
                  <span className="text-xs font-medium text-[#007a33]">
                    {msg.sender_name || (msg.role === 'agent' ? 'Support Agent' : 'AI Assistant')}
                  </span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                {formatTime(msg.timestamp)}
              </p>
            </div>
          </div>
        ))}
        
        {/* Typing indicator */}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-gray-100">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Email transcript form */}
      {showEmailForm && (
        <div className="p-3 border-t bg-gray-50">
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEmailForm(false)} className="p-1">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <Input
              type="email"
              placeholder="Enter your email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              className="flex-1 text-sm"
            />
            <Button size="sm" onClick={handleEmailTranscript} className="bg-[#007a33] hover:bg-[#005c26]">
              Send
            </Button>
          </div>
        </div>
      )}

      {/* Input area */}
      {!showEmailForm && (
        <div className="p-3 border-t bg-white">
          {/* Quick actions */}
          <div className="flex items-center gap-2 mb-2">
            {chatStatus === 'active' && (
              <button
                onClick={handleEscalate}
                className="text-xs text-gray-500 hover:text-[#007a33] flex items-center gap-1 px-2 py-1 rounded-full border border-gray-200 hover:border-[#007a33] transition-colors"
              >
                <Phone className="w-3 h-3" />
                Talk to human
              </button>
            )}
            <button
              onClick={() => setShowEmailForm(true)}
              className="text-xs text-gray-500 hover:text-[#007a33] flex items-center gap-1 px-2 py-1 rounded-full border border-gray-200 hover:border-[#007a33] transition-colors"
            >
              <Mail className="w-3 h-3" />
              Email transcript
            </button>
            <button
              onClick={handleNewChat}
              className="text-xs text-gray-500 hover:text-[#007a33] ml-auto"
            >
              New chat
            </button>
          </div>
          
          {/* Input */}
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              type="text"
              placeholder="Type a message..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              onFocus={() => { isInputFocused.current = true; }}
              onBlur={() => { isInputFocused.current = false; }}
              disabled={isLoading}
              className="flex-1"
              data-testid="chat-input"
            />
            <Button
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="bg-[#007a33] hover:bg-[#005c26] px-3"
              data-testid="chat-send-button"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
